import React, { useState, useEffect, useCallback, useMemo } from "react";
import { IShow } from "@eagerdog/interfaces";
import { Constants } from "@eagerdog/constants";
import { ShowRunTimeSlotDtoId } from "src/components/StepForm/ShowForm/ShowForm";

import { apiService } from "src/services";
import Button from "src/components/Button/Button";
import Dropdown from "src/components/Dropdown/Dropdown";
import { IOption } from "src/components/Dropdown/Dropdown";
import Input from "src/components/Input/Input";
import { TimeInput } from "src/components/TimeInput";
import { Modal, useModal } from "src/components/Modal/Modal";
import { helperService } from "src/services/helper.service";

import Timeslots from "src/components/Timeslots/Timeslots";

import "./TimeslotTab.scss";

interface IProps {
  show?: IShow;
  clubId?: string;
  /** Current show type id — used to enable per-class mode for OSNG/Rally */
  showType?: string;
  /** Class levels offered by this show — drives per-class accordion */
  availableLevels?: string[];
  onChange(timeslots: ShowRunTimeSlotDtoId[], original_timeslots: ShowRunTimeSlotDtoId[]): void;
}

const DURATION_OPTIONS = helperService.timeslotDurationOptions;

const PER_CLASS_SHOW_TYPES = [
  Constants.show_type.obedience_show_n_go,
  Constants.show_type.general_rally,
];

/** Convert "HH:MM" or "HH:MM:SS" string to total minutes from midnight */
function timeToMinutes(hhmm: string | null | undefined): number {
  if (!hhmm) return 0;
  const [h, m] = hhmm.split(":").map(Number);
  return h * 60 + m;
}

/** Format total minutes from midnight as "9:00 AM" / "12:30 PM" */
function minutesToLabel(total: number): string {
  const h24 = Math.floor(total / 60) % 24;
  const min = total % 60;
  const ampm = h24 < 12 ? "AM" : "PM";
  const h12 = h24 === 0 ? 12 : h24 > 12 ? h24 - 12 : h24;
  return `${h12}:${min.toString().padStart(2, "0")} ${ampm}`;
}

/** Generate timeslot label strings between start and end with a given duration */
function generateSlots(
  startMins: number,
  count: number,
  dur: number,
  dogLimit: number
): ShowRunTimeSlotDtoId[] {
  return Array.from({ length: count }, (_, i) => {
    const slotStart = startMins + i * dur;
    const slotEnd = slotStart + dur;
    return {
      time_slot: `${minutesToLabel(slotStart)} - ${minutesToLabel(slotEnd)}`,
      num_available: dogLimit,
      note: "",
    };
  });
}

const TimeslotTab: React.FC<IProps> = (props) => {
  const isPerClassMode =
    !!props.showType &&
    PER_CLASS_SHOW_TYPES.includes(props.showType) &&
    !!props.availableLevels &&
    props.availableLevels.length > 0;

  // ── Flat mode state (all show types except per-class OSNG/Rally) ──────────
  const [loaded, setLoaded] = useState<boolean>(false);
  const [hasReservations, setHasReservations] = useState<boolean>(false);
  const [timeslotsKey, setTimeslotsKey] = useState<number>(0);
  const [originalTimeslots, setOriginalTimeslots] = useState<ShowRunTimeSlotDtoId[]>([]);
  const [timeslots, setTimeslots] = useState<ShowRunTimeSlotDtoId[]>([{ time_slot: "", num_available: 0, note: "" }]);

  // ── Per-class mode state ──────────────────────────────────────────────────
  const [timeslotsByLevel, setTimeslotsByLevel] = useState<Record<string, ShowRunTimeSlotDtoId[]>>({});
  const [originalTimeslotsByLevel, setOriginalTimeslotsByLevel] = useState<Record<string, ShowRunTimeSlotDtoId[]>>({});
  const [hasReservationsByLevel, setHasReservationsByLevel] = useState<Record<string, boolean>>({});
  const [timeslotsKeyByLevel, setTimeslotsKeyByLevel] = useState<Record<string, number>>({});

  // ── Copy-from-show state ──────────────────────────────────────────────────
  const copyModal = useModal();
  const [copyableShows, setCopyableShows] = useState<IOption[]>([]);
  const [selectedCopyShow, setSelectedCopyShow] = useState<IOption>({ value: "None", id: "None" });
  const [isCopying, setIsCopying] = useState<boolean>(false);

  // ── Generator state ───────────────────────────────────────────────────────
  const generatorModal = useModal();
  const confirmModal = useModal();
  const [genStartTime, setGenStartTime] = useState<string | null>("09:00");
  const [genEndTime, setGenEndTime] = useState<string | null>("17:00");
  const [genDuration, setGenDuration] = useState<IOption>(DURATION_OPTIONS[1]);
  const [genDogLimit, setGenDogLimit] = useState<number>(1);
  /** In per-class mode: the level being targeted by the generator */
  const [genLevel, setGenLevel] = useState<string>("");

  // Sync genLevel to first available level when entering per-class mode
  useEffect(() => {
    if (isPerClassMode && props.availableLevels && props.availableLevels.length > 0) {
      setGenLevel((prev) => (prev && props.availableLevels!.includes(prev) ? prev : props.availableLevels![0]));
    }
  }, [isPerClassMode, props.availableLevels]);

  // ── Load timeslots ────────────────────────────────────────────────────────
  useEffect(() => {
    if (loaded) return;

    const doLoad = async () => {
      if (!props.show) {
        if (isPerClassMode) {
          // New show: initialise empty per-level buckets
          const empty: Record<string, ShowRunTimeSlotDtoId[]> = {};
          for (const lvl of props.availableLevels!) empty[lvl] = [];
          setTimeslotsByLevel(empty);
          setOriginalTimeslotsByLevel(empty);
        }
        setLoaded(true);
        return;
      }

      try {
        const response = await apiService.getShowRunTimeSlots(props.show.event_id, props.show._id);

        if (isPerClassMode) {
          // Group returned slots by level
          const byLevel: Record<string, ShowRunTimeSlotDtoId[]> = {};
          for (const lvl of props.availableLevels!) byLevel[lvl] = [];

          let hasAnyRes: Record<string, boolean> = {};
          for (const slot of response) {
            const lvl = slot.level ?? "";
            if (!byLevel[lvl]) byLevel[lvl] = [];
            if ((slot.num_reserved || 0) > 0) hasAnyRes[lvl] = true;
            byLevel[lvl].push({
              id: slot._id,
              time_slot: slot.time_slot,
              num_available: slot.num_available,
              note: slot.note ?? "",
              level: slot.level,
            });
          }

          setTimeslotsByLevel(byLevel);
          setOriginalTimeslotsByLevel(JSON.parse(JSON.stringify(byLevel)));
          setHasReservationsByLevel(hasAnyRes);
        } else {
          let _time_slots: ShowRunTimeSlotDtoId[] = [];
          let _hasRes = false;
          for (const slot of response) {
            if ((slot.num_reserved || 0) > 0) _hasRes = true;
            _time_slots.push({
              id: slot._id,
              time_slot: slot.time_slot,
              num_available: slot.num_available,
              note: slot.note ?? "",
            });
          }
          setHasReservations(_hasRes);
          setOriginalTimeslots(_time_slots);
          setTimeslots(_time_slots);
        }
      } catch {
        // silently ignore
      }

      setLoaded(true);
    };

    doLoad();
  }, [loaded, props.show, isPerClassMode, props.availableLevels]);

  // ── Emit onChange (flat mode) ─────────────────────────────────────────────
  const { onChange } = props;
  useEffect(() => {
    if (!isPerClassMode) {
      onChange(timeslots, originalTimeslots);
    }
  }, [timeslots, originalTimeslots, onChange, isPerClassMode]);

  // ── Per-class: merge all level buckets into flat array and emit ───────────
  const mergedPerClassSlots = useMemo<ShowRunTimeSlotDtoId[]>(() => {
    if (!isPerClassMode) return [];
    return Object.entries(timeslotsByLevel).flatMap(([lvl, slots]) =>
      slots.map((s) => ({ ...s, level: lvl }))
    );
  }, [isPerClassMode, timeslotsByLevel]);

  const mergedOriginalSlots = useMemo<ShowRunTimeSlotDtoId[]>(() => {
    if (!isPerClassMode) return [];
    return Object.entries(originalTimeslotsByLevel).flatMap(([lvl, slots]) =>
      slots.map((s) => ({ ...s, level: lvl }))
    );
  }, [isPerClassMode, originalTimeslotsByLevel]);

  useEffect(() => {
    if (isPerClassMode) {
      onChange(mergedPerClassSlots, mergedOriginalSlots);
    }
  }, [mergedPerClassSlots, mergedOriginalSlots, onChange, isPerClassMode]);

  const handleLevelTimeslotsChange = useCallback(
    (level: string, slots: ShowRunTimeSlotDtoId[]) => {
      setTimeslotsByLevel((prev) => ({ ...prev, [level]: slots }));
    },
    []
  );

  // ── Copy from another show ────────────────────────────────────────────────
  const openCopyModal = useCallback(async () => {
    if (!props.show || !props.clubId) return;
    try {
      const shows = await apiService.getClubEventShows(props.clubId, props.show.event_id);
      const options: IOption[] = shows
        .filter((s: IShow) => s._id !== props.show!._id && s.show_type === props.show!.show_type)
        .map((s: IShow) => ({ id: s._id, value: s.show_name || s._id }));
      setCopyableShows(options);
      setSelectedCopyShow({ value: "None", id: "None" });
      copyModal.toggle();
    } catch {
      // silently ignore
    }
  }, [props.show, props.clubId, copyModal]);

  const applyCopy = useCallback(async () => {
    if (!props.show || selectedCopyShow.id === "None") return;
    setIsCopying(true);
    try {
      const slots = await apiService.getShowRunTimeSlots(props.show.event_id, selectedCopyShow.id);
      if (isPerClassMode) {
        const byLevel: Record<string, ShowRunTimeSlotDtoId[]> = {};
        for (const lvl of (props.availableLevels ?? [])) byLevel[lvl] = [];
        for (const slot of slots) {
          const lvl = slot.level ?? "";
          if (!byLevel[lvl]) byLevel[lvl] = [];
          byLevel[lvl].push({ time_slot: slot.time_slot, num_available: slot.num_available, note: slot.note ?? "" });
        }
        setTimeslotsByLevel(byLevel);
        setTimeslotsKeyByLevel((prev) =>
          Object.fromEntries(Object.keys(byLevel).map((lvl) => [lvl, (prev[lvl] ?? 0) + 1]))
        );
      } else {
        const copied: ShowRunTimeSlotDtoId[] = slots.map((s) => ({
          time_slot: s.time_slot,
          num_available: s.num_available,
          note: s.note ?? "",
        }));
        if (copied.length > 0) {
          setTimeslots(copied);
          setTimeslotsKey((k) => k + 1);
        }
      }
    } catch {
      // silently ignore
    } finally {
      setIsCopying(false);
      copyModal.toggle();
    }
  }, [props.show, props.availableLevels, selectedCopyShow, copyModal, isPerClassMode]);

  // ── Generator ─────────────────────────────────────────────────────────────
  const genPreviewCount = useMemo(() => {
    const startMins = timeToMinutes(genStartTime);
    const endMins = timeToMinutes(genEndTime);
    const dur = parseInt(genDuration.id, 10);
    if (isNaN(dur) || dur <= 0 || endMins <= startMins) return 0;
    return Math.floor((endMins - startMins) / dur);
  }, [genStartTime, genEndTime, genDuration]);

  const isGenValid = genPreviewCount > 0 && genDogLimit >= 1;

  const applyGenerate = useCallback(() => {
    const startMins = timeToMinutes(genStartTime);
    const dur = parseInt(genDuration.id, 10);
    const generated = generateSlots(startMins, genPreviewCount, dur, genDogLimit);

    if (isPerClassMode) {
      setTimeslotsByLevel((prev) => ({ ...prev, [genLevel]: generated }));
      setTimeslotsKeyByLevel((prev) => ({ ...prev, [genLevel]: (prev[genLevel] ?? 0) + 1 }));
    } else {
      setTimeslots(generated);
      setTimeslotsKey((k) => k + 1);
    }

    confirmModal.toggle();
    generatorModal.toggle();
  }, [genStartTime, genDuration, genPreviewCount, genDogLimit, genLevel, isPerClassMode, confirmModal, generatorModal]);

  // Level options for the generator dropdown (per-class mode only)
  const levelOptions: IOption[] = useMemo(
    () => (props.availableLevels ?? []).map((lvl) => ({ id: lvl, value: lvl })),
    [props.availableLevels]
  );

  // ── Collapsed sections ────────────────────────────────────────────────────
  const [collapsedLevels, setCollapsedLevels] = useState<Record<string, boolean>>({});
  const toggleLevelCollapse = useCallback((lvl: string) => {
    setCollapsedLevels((prev) => ({ ...prev, [lvl]: !prev[lvl] }));
  }, []);

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div>
      <div className="stepTitle">{props.showType === Constants.show_type.fetch ? "Run Times (optional)" : "Run Times"}</div>
      <p>
        {props.showType === Constants.show_type.fetch
          ? "Run times are optional. Leave this blank and dogs stay in armband order. Add a time only if exhibitors should pick one when they register."
          : "Create timeslots for your attendees to select when registering. Include a label, total amount of dogs that can attend during that timeslot and an optional note."}
      </p>

      {/* Toolbar */}
      <div className="timeslotToolbar">
        <Button
          className="generatorBtn"
          onClick={(e: any) => { e.preventDefault(); generatorModal.toggle(); }}
        >
          Bulk Generate Timeslots
        </Button>
        {props.show && props.clubId && (
          <Button className="copyBtn" onClick={(e: any) => { e.preventDefault(); openCopyModal(); }}>
            Copy from Another Show
          </Button>
        )}
      </div>

      {/* Generator modal */}
      <Modal
        isShown={generatorModal.isShown}
        hide={generatorModal.toggle}
        className="timeslotGeneratorModal"
        modalContent={
          <div className="timeslotGeneratorContent">
            <div className="modalTitle">Bulk Generate Timeslots</div>

            {isPerClassMode && levelOptions.length > 0 && (
              <div className="genRow">
                <Dropdown
                  label="For Class"
                  options={levelOptions}
                  value={{ id: genLevel, value: genLevel }}
                  onChange={(_e: any, val: IOption) => setGenLevel(val.id)}
                  placeholder="Select a class..."
                />
              </div>
            )}

            <div className="genRow">
              <TimeInput
                label="Start Time"
                value={genStartTime}
                onChange={(val) => setGenStartTime(val as string | null)}
              />
              <TimeInput
                label="End Time"
                value={genEndTime}
                onChange={(val) => setGenEndTime(val as string | null)}
              />
            </div>

            <div className="genRow">
              <Dropdown
                label="Duration"
                options={DURATION_OPTIONS}
                value={genDuration}
                onChange={(_e: any, val: IOption) => setGenDuration(val)}
                placeholder=""
              />
              <Input
                type="number"
                min="1"
                label="Dog Limit / Slot"
                defaultValue={genDogLimit.toString()}
                onChange={(e: any) =>
                  setGenDogLimit(Math.max(1, parseInt(e.target.value, 10) || 1))
                }
              />
            </div>

            {genPreviewCount > 0 && (
              <p className="generatorPreview">
                Will generate <strong>{genPreviewCount}</strong> timeslot{genPreviewCount !== 1 ? "s" : ""}{" "}
                ({minutesToLabel(timeToMinutes(genStartTime))} – {minutesToLabel(timeToMinutes(genEndTime))},{" "}
                {genDuration.value} each)
                {isPerClassMode && genLevel && <> for <strong>{genLevel}</strong></>}
              </p>
            )}
            {genPreviewCount === 0 && genStartTime && genEndTime && (
              <p className="generatorError">End time must be after start time.</p>
            )}

            <div className="modalActions">
              <Button
                className="secondary"
                onClick={(e: any) => { e.preventDefault(); generatorModal.toggle(); }}
              >
                Cancel
              </Button>
              <Button
                className="primary"
                disabled={!isGenValid || (isPerClassMode && !genLevel)}
                onClick={(e: any) => { e.preventDefault(); confirmModal.toggle(); }}
              >
                Generate
              </Button>
            </div>
          </div>
        }
      />

      {/* Confirm replace modal */}
      <Modal
        isShown={confirmModal.isShown}
        hide={confirmModal.toggle}
        className="timeslotGeneratorModal"
        modalContent={
          <div className="timeslotGeneratorContent">
            <div className="modalTitle">Replace Timeslots?</div>
            <p className="modalWarning">
              {isPerClassMode
                ? `This will replace all timeslots for "${genLevel}" with ${genPreviewCount} generated slot${genPreviewCount !== 1 ? "s" : ""}.`
                : `This will remove all ${timeslots.length > 0 ? timeslots.length : "existing"} timeslot${timeslots.length !== 1 ? "s" : ""} and replace them with ${genPreviewCount} generated slot${genPreviewCount !== 1 ? "s" : ""}.`}
              {" "}Any manually entered labels or notes will be lost.
            </p>
            <div className="modalActions">
              <Button
                className="secondary"
                onClick={(e: any) => { e.preventDefault(); confirmModal.toggle(); }}
              >
                Cancel
              </Button>
              <Button
                className="primary"
                onClick={(e: any) => { e.preventDefault(); applyGenerate(); }}
              >
                Yes, Replace
              </Button>
            </div>
          </div>
        }
      />

      {/* Copy from Another Show modal */}
      <Modal
        isShown={copyModal.isShown}
        hide={copyModal.toggle}
        modalContent={
          <div className="timeslotGeneratorContent">
            <div className="modalTitle">Copy Timeslots from Show</div>
            {copyableShows.length === 0 ? (
              <p className="modalWarning">No other shows of this type found in this event.</p>
            ) : (
              <>
                <Dropdown
                  label="Select Show"
                  options={[{ value: "None", id: "None" }, ...copyableShows]}
                  value={selectedCopyShow}
                  onChange={(_e: any, val: IOption) => setSelectedCopyShow(val)}
                  placeholder="Select a show..."
                />
                <p className="modalWarning">
                  This will replace all current timeslots with the selected show's setup.
                </p>
              </>
            )}
            <div className="modalActions">
              <Button
                className="secondary"
                onClick={(e: any) => { e.preventDefault(); copyModal.toggle(); }}
              >
                Cancel
              </Button>
              {copyableShows.length > 0 && (
                <Button
                  className="primary"
                  disabled={selectedCopyShow.id === "None" || isCopying}
                  onClick={(e: any) => { e.preventDefault(); applyCopy(); }}
                >
                  {isCopying ? "Copying..." : "Copy"}
                </Button>
              )}
            </div>
          </div>
        }
      />

      {/* Per-class accordion */}
      {isPerClassMode && loaded && (
        <div className="perClassTimeslots">
          {(props.availableLevels ?? []).map((lvl) => {
            const isCollapsed = collapsedLevels[lvl] === true;
                  const slotCount = (timeslotsByLevel[lvl] ?? []).filter((s) => s.time_slot).length;
                  return (
                    <div key={lvl} className={`perClassSection${isCollapsed ? " collapsed" : ""}`}>
                      <button
                        type="button"
                        className="perClassSectionTitle"
                        onClick={() => toggleLevelCollapse(lvl)}
                      >
                        <span className="levelName">{lvl}</span>
                        <span className="slotCount">{slotCount} Time slot{slotCount !== 1 ? "s" : ""}</span>
                        <span className="chevron">{isCollapsed ? "▸" : "▾"}</span>
                      </button>
                {!isCollapsed && (
                  <Timeslots
                    key={`${lvl}-${timeslotsKeyByLevel[lvl] ?? 0}`}
                    timeslots={timeslotsByLevel[lvl] ?? []}
                    disableReorder={hasReservationsByLevel[lvl] === true}
                    onChange={(slots: ShowRunTimeSlotDtoId[]) => handleLevelTimeslotsChange(lvl, slots)}
                  />
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Flat (legacy) timeslot list */}
      {!isPerClassMode && loaded && (
        <Timeslots
          key={timeslotsKey}
          timeslots={timeslots}
          disableReorder={hasReservations}
          onChange={(slots: ShowRunTimeSlotDtoId[]) => setTimeslots(slots)}
        />
      )}
    </div>
  );
};

export default TimeslotTab;
